import {
  ColDef,
  ColumnState,
  createGrid,
  GridApi,
  ValueFormatterParams,
} from '@ag-grid-community/core';
import { AgGridSolidRef } from '@ag-grid-community/solid';
import { CheckCircleIcon } from '@atgfr8/icons';
import { ToastType } from '@components';
import { FeatureFlags } from '@constants/featureFlags';
import { v4ServeDocumentUrl } from '@constants/urls';
import { LineItem } from '@store/accounting/invoicing/types';
import {
  BillingQueuePendingApprovalItem,
  billingQueueStore,
  DocumentRecord,
  gridRefStore,
  setBillingQueueStore,
} from '@store/billingQueue';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { dateFormatter } from '@store/loadboard';
import { createError } from '@utils/errorUtils';
import { handleToast } from '@utils/utils';
import size from 'lodash/size';
import { Accessor, createMemo, createSignal } from 'solid-js';

export const getSortedLineItems = (lineItems: LineItem[]) => {
  return createMemo(() => {
    const stickyItems = ['Line Haul', 'Fuel'];
    const typesLower = stickyItems.map((item) => {
      return item.toLowerCase();
    });

    // line haul should always be first
    const lineHaulLineItem = lineItems.filter((item) => {
      return (
        item.type !== null &&
        (item.type as string).toLowerCase() === 'line haul'
      );
    });

    // then comes fuel here
    const fuelLineItem = lineItems.filter((item) => {
      return (
        item.type !== null && (item.type as string).toLowerCase() === 'fuel'
      );
    });

    // then the rest of the line items ordered by id
    const otherLineItems = lineItems
      .filter((item) => {
        return (
          item.type !== null &&
          !typesLower.includes((item.type as string).toLowerCase())
        );
      })
      .sort((a, b) => a.id - b.id);

    const sortedLineItems = [];
    if (lineHaulLineItem.length > 0) {
      sortedLineItems.push(...lineHaulLineItem);
    }

    if (fuelLineItem.length > 0) {
      sortedLineItems.push(...fuelLineItem);
    }
    sortedLineItems.push(...otherLineItems);
    return sortedLineItems;
  });
};

export const valueFormatter = (params: ValueFormatterParams) => {
  return Boolean(params.value) ? 'Yes' : 'No';
};

export const iconCellRenderer =
  (field: keyof BillingQueuePendingApprovalItem) =>
  (params: { data: BillingQueuePendingApprovalItem }) =>
    params.data[field] === true ? (
      <CheckCircleIcon class="text-green-600" />
    ) : (
      ''
    );

export const downloadZip = async (url: string) => {
  try {
    const response = await fetch(
      `${v4ServeDocumentUrl.doc(encodeURIComponent(url))}`,
      {
        method: 'GET',
        credentials: 'include',
      },
    );
    const ctHeader = response.headers.get('Content-Type');
    const ct = ctHeader !== null ? ctHeader : '';
    if (ct.includes('application/json')) {
      const err = (await response.json()) as {
        Success: boolean;
        Message: string;
      };
      throw new Error(err.Message || 'Download error');
    }

    const blob = await response.blob();
    const filename = url.split('/').pop() ?? 'download.zip';

    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  } catch (error: unknown) {
    const e = createError(error, 'Failed to download Data');
    handleToast(ToastType.Error, e.message);
  }
};

export const caseInsensitiveComparator = (a: string, b: string) => {
  const x = (a || '').toString().toLowerCase();
  const y = (b || '').toString().toLowerCase();

  return x.localeCompare(y);
};

export const updateSortModel = (gridRef: { api?: GridApi | undefined }) => {
  const columnState = gridRef.api?.getColumnState();
  if (Array.isArray(columnState)) {
    const sortModel: { colId: string; sort: 'asc' | null }[] = [];

    columnState.forEach((col: ColumnState) => {
      if (typeof col.colId === 'string') {
        // Copy existing column state and update sort state
        sortModel.push({
          colId: col.colId,
          sort: col.rowGroup === true ? 'asc' : null,
        });
      }
    });

    gridRef.api?.applyColumnState({
      state: sortModel,
      applyOrder: false,
    });
  }
};

export const createGroupValueFormatter =
  (prefix: string) => (params: ValueFormatterParams<unknown, Date>) => {
    if (params.node?.rowGroupColumn?.getColId() === params.colDef.field) {
      if (prefix === 'DateReceived') {
        return `${prefix}: ${dateFormatter(params)}`;
      }
      return `${prefix}: ${params.value}`;
    }
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (params.colDef.field === 'dateReceived') {
      return dateFormatter(params);
    }
    return typeof params.value === 'string' || typeof params.value === 'number'
      ? params.value
      : '';
  };

export const [selectedRow, setSelectedRow] =
  createSignal<DocumentRecord | null>(null);

export const updateTabCounts = () => {
  if (!isFeatureFlagEnabled(FeatureFlags.PortBillingApi)()) {
    setBillingQueueStore(
      'uploadQueue',
      'filteredDataCount',
      gridRefStore.upload?.getDisplayedRowCount() ??
        size(billingQueueStore.uploadQueue.data),
    );
  }
  if (!isFeatureFlagEnabled(FeatureFlags.PortBillingApi)()) {
    setBillingQueueStore(
      'approvedQueue',
      'filteredDataCount',
      gridRefStore.approved?.getDisplayedRowCount() ??
        size(billingQueueStore.approvedQueue.data),
    );
  }
  setBillingQueueStore(
    'loadDocument',
    'filteredDataCount',
    size(billingQueueStore.loadDocument.data),
  );
  if (!isFeatureFlagEnabled(FeatureFlags.PortBillingApi)()) {
    setBillingQueueStore(
      'respondedQueue',
      'filteredDataCount',
      gridRefStore.responded?.getDisplayedRowCount() ??
        size(billingQueueStore.respondedQueue.data),
    );
  }
  setBillingQueueStore(
    'pendingApprovalQueue',
    'filteredDataCount',
    gridRefStore.pending?.getDisplayedRowCount() ??
      size(billingQueueStore.pendingApprovalQueue.data),
  );
  setBillingQueueStore(
    'disputedQueue',
    'filteredDataCount',
    gridRefStore.disputed?.getDisplayedRowCount() ??
      size(billingQueueStore.disputedQueue.data),
  );
};

export const removeItemFromGrid = <
  T extends { flagId: number; orderId: number },
>(
  queueData: T[],
  selectedFlagId: Accessor<number | undefined>,
  gridRef: AgGridSolidRef | undefined,
) => {
  const itemToRemove = queueData.find(
    (item) => item.flagId === selectedFlagId(),
  );

  if (itemToRemove) {
    void gridRef?.api.applyTransactionAsync({
      remove: [itemToRemove],
    });
  }
};

export const getDocumentPath = (url: string): string => {
  if (url.includes('?url=')) {
    return url.substring(url.lastIndexOf('?url=') + 5);
  }
  return url;
};

export const exportRowsToExcel = (
  columnDefs: ColDef[],
  rowData: Record<string, unknown>[],
  fileName: string,
) => {
  const container = document.createElement('div');
  container.style.display = 'none';
  document.body.appendChild(container);

  const api = createGrid(container, {
    columnDefs: columnDefs.filter((column) => column.colId !== 'Action'),
    rowData,
  });

  try {
    api.exportDataAsExcel({ fileName });
  } finally {
    api.destroy();
    container.remove();
  }
};

export const applyDefaultLTLFilter = (gridApi: GridApi) => {
  const currentFilterModel = gridApi.getFilterModel();
  if (!currentFilterModel['lTL']) {
    gridApi.setFilterModel({
      ...currentFilterModel,
      lTL: {
        values: ['false'],
        filterType: 'set',
      },
    });
  }
};
